import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, PartyPopper } from "lucide-react";
import { useGetFinishedOpenEndedQuestionsOfAssignment } from "@packages/api/madlenAPI";
import { Button, Text } from "@core/index";
import { StudentsAnswersTable } from "../components/StudentsAnswersTable";
import Modal from "@core/modal";

const OpenEndedQuestionsReviewView = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Fetch all open-ended questions for this assignment
  const { data, isLoading, error } =
    useGetFinishedOpenEndedQuestionsOfAssignment(Number(assignmentId), {
      query: {
        refetchOnWindowFocus: false,
      },
    });

  const handleBack = () => {
    navigate(`/assignments/${assignmentId}`);
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-32 bg-gray-200 dark:bg-gray-700 rounded"
              ></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <Text variant="h2" className="text-red-500">
          {t("assignments.openEnded.review.error")}
        </Text>
        <Text>
          {error?.message || t("assignments.openEnded.review.genericError")}
        </Text>
        <Button onClick={handleBack} className="mt-4">
          {t("common.back")}
        </Button>
      </div>
    );
  }

  // Ensure studentAssignments is not undefined
  const studentAssignments = data.studentAssignments || [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          onClick={handleBack}
          variant="solid"
          size="icon"
          rounded="lg"
          textColor="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>

        <div>
          <Text variant="h1">{t("assignments.openEnded.review.title")}</Text>
          <Text variant="h2" className="text-primary-600 dark:text-primary-400">
            {data?.assignmentName ||
              t("assignments.openEnded.review.unknownAssignment")}
          </Text>
        </div>
      </div>

      {/* Main content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <StudentsAnswersTable
          studentAssignments={studentAssignments}
          onStudentSelect={(studentId) => {
            navigate(
              `/assignments/${assignmentId}/open-ended-review/sequential/${studentId}`,
            );
          }}
        />
      </div>

      {/* Review All Button */}
      <div className="flex justify-between gap-2">
        <Button
          onClick={() =>
            navigate(
              `/assignments/${assignmentId}/open-ended-review/sequential`,
            )
          }
          variant="solid"
          size="md"
          rounded="lg"
          className="bg-purple-500 hover:bg-purple-700 text-white"
        >
          {t("assignments.openEnded.review.reviewOnebyOne")}
        </Button>
      </div>

      {/* Success Modal */}
      <Modal
        show={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title={t("assignments.openEnded.review.success.title")}
      >
        <div className="p-6 text-center space-y-6">
          <PartyPopper className="h-16 w-16 mx-auto text-yellow-500 animate-bounce" />
          <Text className="text-lg">
            {t("assignments.openEnded.review.success.messageNoExport")}
          </Text>
          <div className="flex justify-center gap-4">
            <Button
              onClick={() => setShowSuccessModal(false)}
              variant="solid"
              size="md"
              rounded="lg"
              className="bg-primary-500 hover:bg-primary-700 text-white"
            >
              {t("assignments.openEnded.review.success.continue")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OpenEndedQuestionsReviewView;
